import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Divider, Input, List, ListItem, ListItemAvatar, ListItemText, MenuItem, Pagination, PaginationItem, Select, TextField, Theme, Typography, useMediaQuery } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { api } from "../../config/apiCaller.config";
import { formatToDateDefault } from "../../utils/formatToDateDefault";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { useWallet } from "../../hooks/useWallet.hook";

dayjs.locale("pt-br");

const ExtractPage = () => {

    const [page, setPage] = useState(1);
    const [extractItems, setExtractItems] = useState([]);
    const [limit] = useState(6);
    const [totalPages, setTotalPages] = useState(0);
    const [from, setFrom] = useState<string | undefined>();
    const [to, setTo] = useState<string | undefined>();
    const [type, setType] = useState<string | undefined>();
    const [description, setDescription] = useState<string | undefined>();

    const [fromInput, setFromInput] = useState<Dayjs | null>();
    const [toInput, setToInput] = useState<Dayjs | null>();
    const [typeInput, setTypeInput] = useState<string>('');
    const [descriptionInput, setDescriptionInput] = useState<string>('');

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

    const { points } = useWallet();

    useEffect(() => {
        void loadExtract();
    } , [page, limit, from, to, type, description]);

    const loadExtract = async () => {
        try {
            const response = await api.get(`/wallet/transactions/customer/me?page=${page}&limit=${limit}${
                from ? `&from=${from}` : ''}${
                    to ? `&to=${to}` : ''}${
                        type ? `&type=${type}` : ''}${
                            description ? `&description=${description}` : ''}`);
            
            if (response.status === 200) {
                setExtractItems(response.data.data);
                setTotalPages(response.data.totalPages)
            }
        } catch (error: any) {
            setExtractItems([]);
            setTotalPages(0);
        }
        
    }

    const checkDescription = (description: string) => {
        switch (description) {
            case 'Points expired':
                return 'Pontos expirados';
            default:
                return description;
        }
    }

    const clearFilters = async () => {
        setPage(1);

        setFromInput(undefined);
        setToInput(undefined);
        setTypeInput('');
        setDescriptionInput('');
    }

    const applyFilters = async () => {
        setPage(1);

        const from = fromInput ? formatToDateDefault(fromInput) : undefined;
        const to = toInput ? formatToDateDefault(toInput) : undefined;
        const type = typeInput ?? undefined;
        const description = descriptionInput ?? undefined;

        setFrom(from);
        setTo(to);
        setType(type);
        setDescription(description);
    }

    return (
        <Box height="100vh" bgcolor="#FFFFFF">
            <Typography variant='h4' color='textPrimary'>Extrato</Typography>
            <Typography variant="subtitle1" color="textSecondary">Aqui você acompanha todas as suas movimentações de pontos</Typography>

            <Box display={'flex'} justifyContent={'center'} p={2}>
                <Typography variant='h6' color='textPrimary'><b>Saldo atual:</b>  {points} pontos</Typography>
            </Box>

            <Box display="flex" justifyContent="center" p={2}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        applyFilters();
                    }}
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        justifyContent: "center"
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                        <Box display="flex" gap={2} flexWrap={isMobile ? "nowrap" : "wrap"}>
                            <DatePicker
                                label="De"
                                disableFuture
                                format="DD/MM/YYYY"
                                value={fromInput}
                                onChange={(newValue) => setFromInput(newValue)}
                            />
                            <DatePicker
                                label="Até"
                                disableFuture
                                format="DD/MM/YYYY"
                                value={toInput}
                                onChange={(newValue) => setToInput(newValue)}
                            />
                        </Box>
                    </LocalizationProvider>

                    <Select displayEmpty value={typeInput} onChange={(e) => setTypeInput(e.target.value as string)} style={{ minWidth: 120 }}>
                        <MenuItem value="" disabled>
                            Tipo
                        </MenuItem>
                        <MenuItem value="spent">Gasto</MenuItem>
                        <MenuItem value="earned">Ganho</MenuItem>
                    </Select>

                    <TextField type="text" variant="outlined" placeholder="Descrição" value={descriptionInput} onChange={(e) => setDescriptionInput(e.target.value)} />

                    <Box display="flex" gap={1}>
                        <Button type="button" variant="contained" style={{ backgroundColor: "#D89274" }} onClick={clearFilters}>
                            Limpar filtros
                        </Button>
                        <Button type="submit" variant="contained" style={{ backgroundColor: "#D89274" }}>
                            Filtrar
                        </Button>
                    </Box>
                </form>
            </Box>

            <Box display={'flex'} justifyContent={'center'} p={2}>
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>

                    {extractItems?.length ? extractItems.map((item: any) => (
                        <React.Fragment key={item._id}>
                            <ListItem alignItems="flex-start">
                                <ListItemAvatar>
                                    
                                        {item.type === 'spent' ? 
                                            <Avatar style={{ backgroundColor: 'red' }}>
                                                <MoneyOffIcon /> 
                                            </Avatar>
                                            : 
                                            <Avatar style={{ backgroundColor: 'green' }}>
                                                <AttachMoneyIcon />
                                            </Avatar>
                                        }
                                    
                                </ListItemAvatar>
                                <ListItemText
                                    primary={<b>{checkDescription(item.description)}</b>}                                    
                                    secondary={
                                        <React.Fragment>
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            sx={{ color: 'text.primary', display: 'inline' }}
                                        >
                                            <b>Data:</b> {new Date(item.createdAt).toLocaleDateString()}
                                        </Typography>
                                        <b> Pontos:</b> {` ${item.points}`}
                                        </React.Fragment>
                                    }
                                />
                            </ListItem>
                            <Divider variant="inset" component="li" />
                        </React.Fragment>
                    )) : <Typography variant="h6" color="textSecondary" textAlign={'center'}>Nenhuma movimentação encontrada</Typography>}
                   
                </List>
            </Box>

            <Box display={'flex'} justifyContent={'center'} p={4}>
                <Pagination
                    count={totalPages}
                    page={page}
                    renderItem={(item) => (
                        <PaginationItem
                            slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                            {...item}
                            onClick={() => {setPage(item.page ?? 1)}}
                        />
                    )}
                />
            </Box>
        </Box>
    );
};

export { ExtractPage };